/**
 * Feb 2026 · FX Rate Master.
 *
 * Central per-currency, date-effective exchange rates. New ESOs whose
 * `fx_rate_to_inr` is left at the default auto-inherit the latest rate
 * from this master, so leadership no longer has to hand-stamp today's
 * USD→INR on every export invoice.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  PlusIcon, TrashIcon, PencilIcon, CurrencyInrIcon, MagnifyingGlassIcon,
  CheckCircleIcon, ArrowsClockwiseIcon, UploadSimpleIcon, DownloadSimpleIcon,
} from "@phosphor-icons/react";

const inpCls = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const lblCls = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block";

const CURRENCIES = ["USD", "EUR", "GBP", "AED", "AUD", "CAD", "CHF", "CNY", "JPY", "SGD"];

const empty = () => ({
  currency: "USD",
  rate_to_inr: "",
  effective_date: new Date().toISOString().slice(0, 10),
  source: "Manual",
  notes: "",
});

export default function FxRateMaster() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/fx-rates/");
      setRows((data || []).sort((a, b) => {
        if (a.currency !== b.currency) return a.currency.localeCompare(b.currency);
        return (b.effective_date || "").localeCompare(a.effective_date || "");
      }));
    } catch (_e) { toast.error("Failed to load FX rates"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty()); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, rate_to_inr: String(r.rate_to_inr) }); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.currency || !form.effective_date || !(Number(form.rate_to_inr) > 0)) {
      toast.error("Currency, effective date and rate > 0 are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        currency: form.currency.toUpperCase(),
        rate_to_inr: Number(form.rate_to_inr),
        effective_date: form.effective_date,
        source: form.source || "Manual",
        notes: form.notes || "",
      };
      if (editing) {
        await api.put(`/fx-rates/${editing.id}`, payload);
        toast.success(`Updated ${payload.currency} @ ₹${payload.rate_to_inr}`);
      } else {
        await api.post("/fx-rates/", payload);
        toast.success(`Added ${payload.currency} @ ₹${payload.rate_to_inr} effective ${payload.effective_date}`);
      }
      setDialogOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!confirm(`Delete ${r.currency} @ ₹${r.rate_to_inr} (${r.effective_date})?`)) return;
    try {
      await api.delete(`/fx-rates/${r.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  // Feb 2026 · Bulk CSV import — finance drops a month of RBI/OANDA rates
  // as one file instead of hand-keying each day. Backend upserts on
  // (currency, effective_date) so re-uploads stay idempotent.
  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/fx-rates/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [`${data.created} new`, `${data.updated} updated`];
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(`FX Import · ${parts.join(" · ")}`);
      if ((data.errors || []).length) {
        console.warn("FX import errors:", data.errors);
        toast.info(`${data.errors.length} row(s) skipped — see console`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
  };

  const downloadTemplate = () => {
    // Two example rows so finance sees both the ISO and DD/MM/YYYY date formats
    // the importer accepts. Delivered as a browser download for one-click ease.
    const csv = [
      "currency,rate_to_inr,effective_date,source,notes",
      "USD,84.75,2026-02-10,RBI,February reference rate",
      "EUR,92.10,10/02/2026,OANDA,Spot",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "fx-rates-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };


  // Compute the "current" rate per currency = latest effective_date <= today.
  const today = new Date().toISOString().slice(0, 10);
  const currentByCurrency = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if ((r.effective_date || "") > today) return;
      if (!map[r.currency] || (r.effective_date || "") > (map[r.currency].effective_date || "")) map[r.currency] = r;
    });
    return map;
  }, [rows, today]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(r => [r.currency, r.source, r.notes].filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [rows, q]);

  return (
    <div className="p-6 space-y-5" data-testid="fx-rate-master-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Master Data · Finance</div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-3">
            <CurrencyInrIcon size={26} weight="bold" className="text-[var(--accent)]" />
            FX Rate Master
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Per-currency, date-effective INR conversion rates. New ESOs auto-inherit the latest rate on save — override on the ESO to lock a one-off customer rate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="new-fx-btn" onClick={openNew}
            className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" />New FX Rate
          </button>
          <label
            data-testid="fx-import-btn"
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2 cursor-pointer"
            title="Bulk import — CSV with columns: currency, rate_to_inr, effective_date, source, notes"
          >
            <UploadSimpleIcon size={13} weight="bold" />Import CSV
            <input type="file" accept=".csv,text/csv" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) { importCsv(f); e.target.value = ""; } }} />
          </label>
          <button data-testid="fx-template-btn" onClick={downloadTemplate}
            className="h-10 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2"
            title="Download the CSV template so finance knows the expected columns">
            <DownloadSimpleIcon size={13} weight="bold" />Template
          </button>
          <button data-testid="fx-refresh" onClick={load}
            className="h-10 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={13} weight="bold" />Refresh
          </button>
        </div>
      </div>

      {/* Current rate cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {Object.keys(currentByCurrency).length === 0 ? (
          <div className="tactical-card p-4 text-xs text-zinc-500 col-span-full">No FX rates stamped yet — click <b className="text-white">New FX Rate</b> to add the first one. ESOs will keep using their per-order stamped rate (default 83.0) until you seed at least USD.</div>
        ) : (
          Object.values(currentByCurrency).sort((a, b) => a.currency.localeCompare(b.currency)).map(r => (
            <div key={r.currency} className="tactical-card p-4" data-testid={`fx-current-${r.currency}`}>
              <div className="overline mb-1">{r.currency} → INR</div>
              <div className="font-mono text-3xl font-semibold text-white">₹{Number(r.rate_to_inr).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">as of {r.effective_date} · {r.source || "Manual"}</div>
            </div>
          ))
        )}
      </div>

      {/* History table */}
      <div className="tactical-card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlassIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="fx-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search currency / source / notes…"
              className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
          </div>
          <span className="ml-auto text-xs text-zinc-500">{filtered.length} of {rows.length}</span>
        </div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Currency</th>
                <th className="px-3 py-2 text-right">Rate → INR</th>
                <th className="px-3 py-2 text-left">Effective Date</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Notes</th>
                <th className="px-3 py-2 text-left">Current?</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-xs">
                  {rows.length === 0 ? "No rates yet — add the first one to enable ESO auto-inherit." : "No rates match the search."}
                </td></tr>
              )}
              {filtered.map((r, i) => {
                const isCurrent = currentByCurrency[r.currency]?.id === r.id;
                return (
                  <tr key={r.id} data-testid={`fx-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                    <td className="px-3 py-2 font-mono text-[var(--accent)] text-sm">{r.currency}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-sm">₹{Number(r.rate_to_inr).toFixed(4)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-300">{r.effective_date}</td>
                    <td className="px-3 py-2 text-xs">{r.source || "Manual"}</td>
                    <td className="px-3 py-2 text-xs text-zinc-400 max-w-[280px] truncate" title={r.notes}>{r.notes || "—"}</td>
                    <td className="px-3 py-2">
                      {isCurrent
                        ? <span className="inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Current</span>
                        : <span className="text-zinc-600 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <RowActions
                        testid={`fx-actions-${i}`}
                        items={[
                          { label: "Edit", testid: `fx-edit-${i}`, onClick: () => openEdit(r) },
                          { label: "Delete", tone: "danger", testid: `fx-del-${i}`, onClick: () => remove(r) },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-lg" data-testid="fx-dialog" description="Add or edit an FX rate — spot rate against INR with effective date.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <CurrencyInrIcon size={20} weight="bold" className="text-[var(--accent)]" />
              {editing ? "Edit FX Rate" : "New FX Rate"}
            </DialogTitle>
          </DialogHeader>
          <form id="fx-form" onSubmit={submit} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lblCls}>Currency *</label>
                <select data-testid="fx-currency" value={form.currency}
                  onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                  className={inpCls} required>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={lblCls}>Rate → INR *</label>
                <input data-testid="fx-rate" type="number" step="0.0001" min="0.0001"
                  value={form.rate_to_inr}
                  onChange={e => setForm(f => ({ ...f, rate_to_inr: e.target.value }))}
                  className={inpCls} placeholder="83.5000" required />
              </div>
              <div>
                <label className={lblCls}>Effective Date *</label>
                <input data-testid="fx-effective" type="date" value={form.effective_date}
                  onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))}
                  className={inpCls} required />
              </div>
              <div>
                <label className={lblCls}>Source</label>
                <input data-testid="fx-source" type="text" value={form.source}
                  onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                  className={inpCls} placeholder="RBI · OANDA · Manual" />
              </div>
              <div className="col-span-2">
                <label className={lblCls}>Notes</label>
                <input data-testid="fx-notes" type="text" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className={inpCls} placeholder="Optional context (e.g. quarterly review, spot rate)" />
              </div>
            </div>
          </form>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setDialogOpen(false)}
              className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Cancel</button>
            <button type="submit" form="fx-form" data-testid="fx-save"
              disabled={saving}
              className="h-10 px-5 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
              <CheckCircleIcon size={14} weight="bold" />
              {saving ? "Saving…" : editing ? "Update Rate" : "Save Rate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
